/**
 * 編集セッション（索引 Issue #498 の段階 3 → #722）。**ページが触る唯一の入口**。
 *
 *   const editor = createEditor({ registry });
 *   editor.setSource(sourceOptions(source))                 // ソース（アンカー・整列・状態チャンネル）
 *   editor.dispatch({ type: 'anchor/move', moves })         // 文書を変えるのはコマンドだけ（`commands.js`）
 *   editor.frameAt(editor.transport.ms)                     // 描くもの
 *   editor.subscribe(fn)                                    // 変わったら呼ばれる
 *
 * 持っているのは 文書 / 再生位置 / 履歴 の 3 つ。タイムラインは**文書かソースが変わったときだけ**組み直す。
 * **時計は読まない**（再生位置は `ports/clock.js::connectClock` が時計から押し込む）。
 */
import { DEFAULT_FPS, snapToFrame } from './time';
import { emptyDocument, readDocument } from './document';
import { EMPTY_REGISTRY } from './types';
import { buildTimeline } from './timeline';
import { frameAt } from './frame';
import { apply } from './commands';
import { canRedo, canUndo, emptyHistory, record, redo, undo } from './history';

export function createEditor({ fps = DEFAULT_FPS, registry = EMPTY_REGISTRY, initialDoc = null } = {}) {
  let doc = readDocument(initialDoc ?? emptyDocument(fps), fps);
  // 再生位置。`playing` は時計が再生中か（SE を鳴らすか・fps の表示）
  let tp = { ms: 0, playing: false };
  let history = emptyHistory();
  let src = { anchors: [], ctx: {}, strategy: undefined, states: [] };
  let cache = null;
  const listeners = new Set();

  const notify = () => { listeners.forEach((fn) => fn(api)); };
  const invalidate = () => { cache = null; };

  const timeline = () => {
    if (!cache) cache = buildTimeline({ doc, registry, ...src });
    return cache;
  };

  /** 文書を差し替えて 1 手を積む（取り消しに残すのは文書だけ。再生位置は戻さない） */
  const commit = (next) => {
    history = record(history, doc);
    doc = next;
    invalidate();
    notify();
  };

  const api = {
    // ---- 読む
    get doc() { return doc; },
    get timeline() { return timeline(); },
    get transport() { return tp; },
    get registry() { return registry; },
    get canUndo() { return canUndo(history); },
    get canRedo() { return canRedo(history); },
    frameAt: (ms) => frameAt(ms, timeline()),

    // ---- 編集（コマンドだけ）
    dispatch(command) {
      const res = apply(doc, command, { timeline: timeline() });
      if (res.ok && res.changed) commit(res.doc);
      return { ok: res.ok, reason: res.reason, changed: res.changed };
    },
    /** まとめて 1 手にする（途中で 1 つでも断られたら全部やめる） */
    dispatchAll(commands = []) {
      let next = doc;
      for (const c of commands) {
        const res = apply(next, c, { timeline: timeline() });
        if (!res.ok) return { ok: false, reason: res.reason, changed: false };
        next = res.doc;
      }
      if (next === doc) return { ok: true, reason: null, changed: false };
      commit(next);
      return { ok: true, reason: null, changed: true };
    },
    undo() {
      const hit = undo(history, doc);
      if (!hit) return false;
      history = hit.history;
      doc = hit.snapshot;
      invalidate();
      notify();
      return true;
    },
    redo() {
      const hit = redo(history, doc);
      if (!hit) return false;
      history = hit.history;
      doc = hit.snapshot;
      invalidate();
      notify();
      return true;
    },

    // ---- 再生位置（取り消しの対象ではない）
    // **変わらなければ知らせない**（知らせる → 描き直す → 同じものを入れ直す、の輪を作らないため。React error #185）
    seek(ms, { playing = tp.playing } = {}) {
      const next = Math.max(0, snapToFrame(Number.isFinite(ms) ? ms : 0, doc.fps));
      if (next === tp.ms && playing === tp.playing) return tp;
      tp = { ms: next, playing };
      notify();
      return tp;
    },

    // ---- ソース・読み込み
    setSource({ anchors = src.anchors, ctx = src.ctx, strategy = src.strategy, states = src.states } = {}) {
      src = { anchors, ctx, strategy, states };
      invalidate();
      notify();
    },
    /** 文書を読み込み直す（履歴は捨てる。前の試合の文書へ戻らないように） */
    load(next) {
      doc = readDocument(next, fps);
      history = emptyHistory();
      invalidate();
      notify();
    },

    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
  };
  return api;
}
